import { useCallback, useMemo, useState } from "react";

import { subMonths } from "date-fns";
import arrayMutators from "final-form-arrays";
import { Field, Form, FormSpy } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { generatePath, useHistory } from "react-router-dom";

import { saveStaticSearch } from "@/api/endpoints/search";
import Chatbubble from "@/assets/icons/chat-bubble.svg";
import ChevronRight from "@/assets/icons/chevron-right.svg";
import CloseIcon from "@/assets/icons/close.svg";
import SearchIcon from "@/assets/icons/magnifying-glass.svg";
import PlusIcon from "@/assets/icons/plus.svg";
import ArchiveIcon from "@/assets/icons/rectangle-stack.svg";
import { MENTION_SOURCE_TYPE } from "@/constants";
import { useMessages } from "@/containers/Messages/MessageContext";
import { useAlert } from "@/contexts/AlertContext";
import { useCapabilities } from "@/contexts/CapabilitiesContext";
import { useI18n } from "@/contexts/i18nContext";
import { FEATURE } from "@/features";
import { useHasFeature } from "@/hooks/useHasFeature";
import { useOrganization } from "@/hooks/useOrganization";
import { ROUTES } from "@/routes_new";
import { UnsavedSearch } from "@/types/Search";
import { UnsavedQuery } from "@/types/SearchQuery";
import { formatDateForApi, pluralize } from "@/utils";
import { stringifyUrl } from "@/utils/query-string";
import { EXPLORE_DATE_RANGES } from "@/views/ArchiveSearch/ranges";
import AccessControl from "@components/AccessControl";
import Button from "@components/Button";
import { FF_DateRange } from "@components/Field";
import TextButton from "@components/TextButton";
import Tooltip from "@components/Tooltip";
import Typography from "@components/Typography";
import { i18n } from "@lingui/core";

import CreateAlertModal from "../CreateAlertModal";
import SearchQuery from "../SearchQuery";
import { DEFAULT_QUERY } from "../settings";

import styles from "./Search.module.css";

/*
  This is needed since we have a combo for start_date and end_date
  in our datepicker. This means we must separate those values outside of the form.
  If we're doing field-level valdidation at some point, we need to do the opposite as
  well.
*/
type FF_ArchiveSearchQuery = Omit<UnsavedSearch, "start_date" | "end_date"> & {
  dates: { startDate: undefined | Date; endDate: undefined | Date };
};

const queryToSearch = ({
  dates,
  ...query
}: FF_ArchiveSearchQuery): UnsavedSearch => {
  return {
    ...query,
    start_date: dates.startDate ? formatDateForApi(dates.startDate) : undefined,
    end_date: dates.endDate ? formatDateForApi(dates.endDate) : undefined,
  };
};

const Search: React.FunctionComponent<{
  onSearch: (values: UnsavedSearch) => Promise<unknown>;
  hasResults?: boolean;
}> = ({ onSearch, hasResults }) => {
  const { locale } = useI18n();
  const { data: orgData } = useOrganization();
  const { plan = null } = orgData?.ok ? orgData.data : {};
  const history = useHistory();
  const canCreateSearches = useHasFeature(FEATURE.CAN_CREATE_SEARCHES);
  const [advanced, setAdvanced] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const { capabilities } = useCapabilities();

  const { refresh } = useAlert();
  const { danger } = useMessages();

  const userDefaultQuery = useMemo<UnsavedQuery>(
    () => ({
      ...DEFAULT_QUERY,
      languages: capabilities.languages,
      sources: capabilities.sources_audio,
      mention_types: MENTION_SOURCE_TYPE.SPEECH,
    }),
    [capabilities]
  );

  const save = useCallback(
    (values: UnsavedSearch) => {
      return saveStaticSearch(values).then(async (res) => {
        if (res.status === 201 && res.data?.uuid) {
          const { uuid, start_date, end_date } = res.data;
          const query = { start_date, end_date };
          history.push(
            stringifyUrl({
              url: generatePath(ROUTES.MENTION_DETAIL, { uuid: uuid }),
              query,
            })
          );
          await refresh();
        } else if (res.status === 500) {
          danger({
            title: L.CUSTOMERS_SEARCH_500_ERROR_TITLE,
            message: L.CUSTOMERS_SEARCH_500_ERROR_MESSAGE,
          });
        } else {
          return res; // What to do with these?
        }
      });
    },
    [history, danger, refresh]
  );

  const handleSaveStaticSearch = useCallback(
    (values: UnsavedSearch) => {
      return save(values);
    },
    [save]
  );

  const dateRanges = EXPLORE_DATE_RANGES.map((obj) => ({
    ...obj,
    label: i18n._(obj.label),
  }));

  return (
    <Form<FF_ArchiveSearchQuery>
      mutators={{
        ...arrayMutators,
      }}
      keepDirtyOnReinitialize
      onSubmit={(values) => onSearch(queryToSearch(values))}
      initialValues={{
        dates: {
          startDate: subMonths(new Date(), 3),
          endDate: new Date(),
        },
        queries: [userDefaultQuery],
      }}
    >
      {({
        handleSubmit,
        submitting,
        dirtySinceLastSubmit,
        submitError,
        invalid,
        pristine,
        form: {
          mutators: { push, remove },
        },
      }) => (
        <form onSubmit={handleSubmit}>
          {submitError && (
            <Typography className={styles.error}>{submitError}</Typography>
          )}
          <FieldArray name="queries">
            {({ fields }) =>
              fields.map((name, index) => (
                <div key={index} className={styles.container}>
                  <SearchQuery name={name} advanced={advanced} />
                  {!advanced && (
                    <TextButton
                      icon={ChevronRight}
                      color="inherit"
                      data-testid="advanced-button"
                      className={styles.advanced}
                      onClick={() => setAdvanced(true)}
                    >
                      {L.EXPLORE_ADVANCED_CTA}
                    </TextButton>
                  )}
                  {index > 0 && (
                    <Tooltip content={L.EXPLORE_QUERY_REMOVE}>
                      <Button
                        className={styles.removeQuery}
                        icon={CloseIcon}
                        variant="secondary"
                        onClick={() => remove("queries", index)}
                      />
                    </Tooltip>
                  )}
                </div>
              ))
            }
          </FieldArray>

          {advanced && (
            <div className={styles.addQuery}>
              <Tooltip content={L.EXPLORE_QUERY_ADD}>
                <Button
                  icon={PlusIcon}
                  variant="primary"
                  data-testid="add-query-button"
                  onClick={() => push("queries", userDefaultQuery)}
                />
              </Tooltip>
            </div>
          )}
          <div className={styles.globals}>
            <Field
              label={L.EXPLORE_DATES}
              render={FF_DateRange}
              locale={locale}
              name="dates"
              ranges={dateRanges}
            />

            <div className={styles.submit}>
              <AccessControl>
                <FormSpy<FF_ArchiveSearchQuery> subscription={{ values: true }}>
                  {({ values }) => (
                    <Button
                      data-testid="save-search-results-button"
                      variant="tertiary"
                      type="button"
                      icon={ArchiveIcon}
                      iconPlacement="after"
                      disabled={!hasResults}
                      onClick={() =>
                        handleSaveStaticSearch(queryToSearch(values))
                      }
                    >
                      {L.EXPLORE_SAVE_MENTIONS_CTA}
                    </Button>
                  )}
                </FormSpy>
                <Tooltip
                  passThrough={canCreateSearches}
                  content={`${
                    L.CREATE_NEW_ALERT_DISABLED_INFO_TEXT_BEGINNING
                  } ${plan?.max_num_alerts} ${pluralize(
                    plan?.max_num_alerts,
                    L.NAV_CREATE_NEW_ALERT_DISABLED_ALERT_SINGULAR,
                    L.NAV_CREATE_NEW_ALERT_DISABLED_ALERT_PLURAL
                  )}`}
                >
                  <Button
                    data-testid="create-alert-button"
                    variant="tertiary"
                    type="button"
                    icon={Chatbubble}
                    iconPlacement="after"
                    disabled={
                      !canCreateSearches ||
                      pristine ||
                      submitting ||
                      (invalid && !dirtySinceLastSubmit)
                    }
                    onClick={() => setCreateModalOpen(true)}
                  >
                    {L.EXPLORE_CREATE_ALERT_CTA}
                  </Button>
                </Tooltip>
              </AccessControl>
              <Button
                data-testid="search-button"
                variant="primary"
                type="submit"
                icon={SearchIcon}
                iconPlacement="after"
                disabled={
                  pristine || submitting || (invalid && !dirtySinceLastSubmit)
                }
              >
                {L.EXPLORE_PERFORM_SEARCH_CTA}
              </Button>
            </div>
          </div>

          <FormSpy<FF_ArchiveSearchQuery> subscription={{ values: true }}>
            {({ values }) => (
              <CreateAlertModal
                /** Set end_date to undefined here. You probably want the search to not end today. */
                search={{ ...queryToSearch(values), end_date: undefined }}
                show={createModalOpen}
                onHide={() => setCreateModalOpen(false)}
              />
            )}
          </FormSpy>
        </form>
      )}
    </Form>
  );
};

export default Search;
